import { JSX, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";

import { Event } from "utils/history/Event";

import {
	Role,
	UserRights
} from "core/blueprint/trajectory/TrajectoryBlueprintService";
import AdminRoleTable from "core/trajectory/access/AdminRoleTable";
import UserRightsTable from "core/trajectory/access/UserRightsTable";

export type AccessProps = {
	users: UserRights[];
	roles: Role[];

	onChangeRoles: (roles: Role[]) => void;
	onChangeUsers: (users: UserRights[]) => void;
};

function Access(props: AccessProps): JSX.Element {
	const { t } = useTranslation();

	const [selectedTab, setSelectedTab] = useState(0);

	const changeSelectedTab = useCallback(
		(_event: React.SyntheticEvent, newValue: number): void => {
			setSelectedTab(newValue);
		},
		[]
	);

	const onChangeUsers = useCallback(
		(event: Event): void => {
			const access = { users: props.users };
			const newAccess = event.toAction().apply(access);
			props.onChangeUsers(newAccess.users);
		},
		[props.users, props.onChangeUsers]
	);

	const onInsertRole = useCallback(
		(role: Role): void => {
			props.onChangeRoles(props.roles.concat(role));
		},
		[props.roles, props.onChangeRoles]
	);

	const onChangeRole = useCallback(
		(event: Event): void => {
			const access = { roles: props.roles };
			const newAccess = event.toAction().apply(access);
			props.onChangeRoles(newAccess.roles);
		},
		[props.roles, props.onChangeRoles]
	);

	const onDeleteRole = useCallback(
		(index: number): void => {
			props.onChangeRoles(props.roles.splice(index, 1));
		},
		[props.roles, props.onChangeRoles]
	);

	const validateRole = useCallback(
		(name: string): boolean =>
			name.trim() !== "" && !props.roles.some(role => role.name === name),
		[]
	);

	return (
		<>
			<Tabs centered value={selectedTab} onChange={changeSelectedTab}>
				<Tab label={t("user_other")}></Tab>
				<Tab label={t("role_other")}></Tab>
			</Tabs>

			{selectedTab === 0 && (
				<UserRightsTable
					users={props.users}
					roles={props.roles.map(role => role.name)}
					editable={true}
					onChange={onChangeUsers}
				/>
			)}

			{selectedTab === 1 && (
				<AdminRoleTable
					roles={props.roles}
					editable={true}
					isValid={validateRole}
					onInsert={onInsertRole}
					onChange={onChangeRole}
					onDelete={onDeleteRole}
				/>
			)}
		</>
	);
}

export default Access;
